import { Combobox } from '@headlessui/react';
import { useEffect, useRef } from 'react';

import { useTranslate } from '../../hooks/useTranslate';
import { CocoSharpInput } from '../CocoSharpText/CocoSharpText';
import { Suggestions } from './components/Suggestions/Suggestions';
import {
  useDestinationSearch,
  useGroupedItems,
  useSearchResults,
} from './DestinationSearch.hooks';
import {
  Dropdown,
  Field,
  Form,
  Icon,
  Input,
  SearchButton,
} from './DestinationSearch.styles';
import { isValidLocalStorageValue } from './DestinationSearch.utils';

type Props = {
  placeholder: string;
  hideAmeliaChat?: boolean;
  isWhitelabel?: boolean;
  initialSearchText?: string;
  tripPlannerExtraQueryParams?: Record<string, string>;
};

export const DestinationSearch = ({
  placeholder,
  hideAmeliaChat,
  isWhitelabel,
  initialSearchText,
  tripPlannerExtraQueryParams,
}: Props) => {
  const { t } = useTranslate();
  const isInitialSearchUpdate = useRef(false);
  const {
    inputRef,
    destination,
    setDestination,
    query,
    setQuery,
    forceNoResults,
    onSubmit,
    setDestinationSearchOptions,
    localStorageValue,
    setLocalStorageValue,
    onRemove,
    lastOptionsGroupedByCategory,
    skipNextRedirect,
  } = useDestinationSearch(tripPlannerExtraQueryParams);
  const { resultsMap, isLoading } = useSearchResults(query);
  const groupedItems = useGroupedItems(resultsMap);
  const lastOptions = useGroupedItems(lastOptionsGroupedByCategory);

  useEffect(() => {
    if (!localStorageValue) {
      return;
    }

    if (!isValidLocalStorageValue(localStorageValue)) {
      setLocalStorageValue([]);
      return;
    }
    setDestinationSearchOptions(localStorageValue);
  }, [localStorageValue]);

  useEffect(() => {
    if (initialSearchText) {
      setQuery(initialSearchText);
    }
  }, [initialSearchText]);

  useEffect(() => {
    if (
      initialSearchText &&
      groupedItems[0]?.items[0] &&
      (!destination || destination.label !== initialSearchText) &&
      query === initialSearchText
    ) {
      isInitialSearchUpdate.current = true;
      skipNextRedirect();
      setDestination({ ...groupedItems[0].items[0], indexOfType: 0 });
    }
  }, [resultsMap, initialSearchText, query]);

  return (
    <Form>
      <Combobox
        as={Field}
        value={destination}
        immediate={true}
        onChange={setDestination}
      >
        <Icon />

        <Input
          as={CocoSharpInput}
          ref={inputRef}
          displayValue={() => destination?.label ?? query ?? ''}
          isWhitelabel={isWhitelabel}
          // @ts-expect-error missing placeholder attr on Input's type
          placeholder={placeholder}
          autoComplete="off"
          onChange={(event) => {
            setQuery(event.target.value);
            if (destination && event.target.value !== destination.label) {
              setDestination(null);
            }
          }}
          aria-label={t('explore.destinationSearch.destination.label')}
        />

        <Dropdown anchor="bottom start" modal={false}>
          <Suggestions
            query={query}
            groupedItems={groupedItems}
            lastOptions={lastOptions}
            isLoading={isLoading}
            forceNoResults={forceNoResults}
            hideAmeliaChat={hideAmeliaChat}
            onRemoveItem={onRemove}
          />
        </Dropdown>
      </Combobox>

      <SearchButton variant="important" onClick={() => onSubmit(groupedItems)}>
        {t('explore.searchButton')}
      </SearchButton>
    </Form>
  );
};
